import { Icon, IconName } from '@/components/Icon';
import theme from '@/config/theme';
import { useNavigation } from '@react-navigation/native';
import {
  ReactElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Platform, Pressable, useWindowDimensions, View } from 'react-native';
import { Text } from '@/components/Text';
import { ControlGroup, ExerciseControls } from './ExerciseControls';
import { isPatientApp } from '@/config/constants';
import { IS_WEB } from '@/utils/platform';
import { styles } from './ExerciseNavigationHeader.styles';

interface ExerciseNavigationHeaderProps {
  skipTitle: string;
  isSelf: boolean;
  exerciseRatingButtons?: ReactElement[];
  exerciseHelpButtons?: ReactElement[];
  exerciseClass: {
    iconName?: string;
    exerciseSettingsKey?: string;
    label?: string;
  };
  onPressHome: () => void;
  onPressSkip: () => void;
  activeExerciseClass: 'word' | 'sentence' | 'text';
}

interface HeaderTitleProps {
  isSelf: boolean;
  icon: IconName;
  selfTrainingTitle: string;
  title: string;
}

function HeaderTitle({
  isSelf = false,
  icon,
  selfTrainingTitle,
  title,
}: HeaderTitleProps) {
  const color = !isPatientApp
    ? theme.colors.white
    : isSelf
      ? theme.colors.white
      : theme.colors.darkGrey;

  return (
    <View style={styles.titleRow}>
      <Icon name={icon} size={isPatientApp ? 32 : 24} color={color} />
      <Text style={[styles.titleText, { color }]}>
        {isSelf ? selfTrainingTitle : title}
      </Text>
    </View>
  );
}

export function ExerciseNavigationHeader(props: ExerciseNavigationHeaderProps) {
  const screenSizeLimit = 1023;

  const {
    isSelf = false,
    exerciseClass,
    exerciseRatingButtons,
    exerciseHelpButtons,
    activeExerciseClass,
  } = props;
  const navigation = useNavigation();
  const headerTitleRef = useRef<View | null>(null);
  const actionsButtonRef = useRef<View | null>(null);

  const [isOverlapping, setIsOverlapping] = useState(false);

  const { width } = useWindowDimensions();

  useEffect(() => {
    if (width <= screenSizeLimit) {
      setIsOverlapping(true);
    }
  }, []);

  useLayoutEffect(() => {
    const tintColor = !isPatientApp
      ? theme.colors.white
      : isSelf
        ? theme.colors.white
        : theme.colors.black;
    navigation.setOptions({
      headerStyle: {
        ...(Platform.OS !== 'web' && !isSelf && styles.isUpsideDown),
        height: 64,
        backgroundColor: isSelf ? theme.appTheme.primary : theme.colors.grey,
      },
      headerTitleStyle: {
        fontSize: 28,
        fontWeight: '600',
        color: tintColor,
      },
      headerLeftContainerStyle: {
        maxWidth: isSelf || activeExerciseClass === 'text' ? null : '5%',
        paddingLeft: 16,
      },
      headerTitle: () =>
        isSelf && (
          <View ref={headerTitleRef}>
            <HeaderTitle
              icon={exerciseClass.iconName as IconName}
              title={exerciseClass.label ?? ''}
              selfTrainingTitle={exerciseClass.exerciseSettingsKey ?? ''}
              isSelf={isSelf}
            />
          </View>
        ),
      headerLeft: () => (
        <>
          <Pressable
            onPress={props.onPressHome}
            testID="home-button"
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Icon name="home" size={36} color={tintColor} />
          </Pressable>
          {!isSelf ? (
            <View ref={headerTitleRef} style={styles.headerTitleIndent}>
              <HeaderTitle
                icon={exerciseClass.iconName as IconName}
                title={!isOverlapping ? (exerciseClass.label ?? '') : ''}
                selfTrainingTitle={exerciseClass.exerciseSettingsKey ?? ''}
                isSelf={isSelf}
              />
            </View>
          ) : null}
        </>
      ),
      headerUpsideDown: !IS_WEB && !isSelf ? true : false,
      headerRight: () => (
        <View
          style={[
            styles.headerRight,
            isPatientApp && styles.headerRightPatient,
          ]}
        >
          {!isSelf && (
            <View ref={actionsButtonRef}>
              <ExerciseControls>
                <ControlGroup>{exerciseHelpButtons}</ControlGroup>
                {(exerciseRatingButtons ?? []).length > 0 && (
                  <ControlGroup>{exerciseRatingButtons}</ControlGroup>
                )}
              </ExerciseControls>
            </View>
          )}
          <Pressable onPress={props.onPressSkip} style={styles.skipButton}>
            <Text style={[styles.skipTitle, { color: tintColor }]}>
              {props.skipTitle}
            </Text>
            <Icon name="arrowRight" size={36} color={tintColor} />
          </Pressable>
        </View>
      ),
    });
  });

  return null;
}
